import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  Tooltip,
  useTheme,
  useMediaQuery,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
} from '@mui/material';
import { dashboardService } from '../../services/dashboardService';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Chat as ChatIcon,
  Analytics as AnalyticsIcon,
  People as PeopleIcon,
  Settings as SettingsIcon,
  History as HistoryIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  Notifications as NotificationsIcon,
  WifiOff as WifiOffIcon,
  Wifi as WifiIcon,
  Schedule as ScheduleIcon,
  Money as MoneyIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ExitToApp as EndShiftIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useWebSocket } from '../../contexts/WebSocketContext';
import NavigationItem from './NavigationItem';
import SystemHealthIndicator from '../SystemHealthIndicator';

const drawerWidth = 280;
const collapsedDrawerWidth = 68;

interface NavigationItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path: string;
  requiredPermission?: string;
  requiredRole?: string | string[];
  badge?: number;
}

const DashboardLayout: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { user, logout, hasPermission, hasRole } = useAuth();
  const {
    isConnected,
    metrics,
    notifications,
    markNotificationAsRead
  } = useWebSocket();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem('sidebarCollapsed') === 'true'
  );
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const [notificationMenuAnchor, setNotificationMenuAnchor] = useState<null | HTMLElement>(null);

  const currentDrawerWidth = sidebarCollapsed ? collapsedDrawerWidth : drawerWidth;

  const handleSidebarToggle = () => {
    setSidebarCollapsed(prev => {
      localStorage.setItem('sidebarCollapsed', String(!prev));
      return !prev;
    });
  };

  const [endShiftDialog, setEndShiftDialog] = useState(false);
  const [endShiftLoading, setEndShiftLoading] = useState(false);
  const [endShiftResult, setEndShiftResult] = useState<{ count: number; message: string } | null>(null);
  const [endShiftError, setEndShiftError] = useState<string | null>(null);

  const handleEndShiftOpen = () => {
    handleUserMenuClose();
    setEndShiftResult(null);
    setEndShiftError(null);
    setEndShiftDialog(true);
  };

  const handleEndShiftConfirm = async () => {
    setEndShiftLoading(true);
    setEndShiftError(null);
    try {
      const result = await dashboardService.releaseAllMyConversations();
      setEndShiftResult(result);
      // Logout automático después de mostrar el mensaje de éxito
      setTimeout(async () => {
        setEndShiftDialog(false);
        await logout();
        navigate('/login');
      }, 2000);
    } catch (err: any) {
      setEndShiftError(err.message || 'Error al finalizar turno');
    } finally {
      setEndShiftLoading(false);
    }
  };

  const handleEndShiftClose = () => {
    setEndShiftDialog(false);
    setEndShiftResult(null);
    setEndShiftError(null);
  };

  // Navegación principal
  const navigationItems: NavigationItem[] = [
    {
      id: 'dashboard',
      label: 'Panel Principal',
      icon: <DashboardIcon />,
      path: '/dashboard',
    },
    {
      id: 'conversations',
      label: 'Conversaciones',
      icon: <ChatIcon />,
      path: '/dashboard/conversations',
      badge: metrics?.activeConversations || 0,
    },
    {
      id: 'windows',
      label: 'Ventanas 24h',
      icon: <ScheduleIcon />,
      path: '/dashboard/windows',
    },
    {
      id: 'metrics',
      label: 'Métricas',
      icon: <AnalyticsIcon />,
      path: '/dashboard/metrics',
      requiredPermission: 'view_metrics',
    },
    {
      id: 'agents',
      label: 'Agentes',
      icon: <PeopleIcon />,
      path: '/dashboard/agents',
      requiredRole: ['ADMIN', 'SUPERVISOR'],
    },
    {
      id: 'debt-bot-configuration',
      label: 'Bot de Deudas',
      icon: <MoneyIcon />,
      path: '/dashboard/debt-bot-configuration',
      requiredRole: 'ADMIN',
    },
    {
      id: 'configuration',
      label: 'Configuración',
      icon: <SettingsIcon />,
      path: '/dashboard/configuration',
      requiredRole: 'ADMIN',
    },
    {
      id: 'audit-logs',
      label: 'Logs de Auditoría',
      icon: <HistoryIcon />,
      path: '/dashboard/audit-logs',
      requiredPermission: 'view_audit_logs',
    },
  ];

  // Filtrar elementos de navegación según permisos
  const filteredNavigationItems = navigationItems.filter(item => {
    if (item.requiredPermission && !hasPermission(item.requiredPermission)) {
      return false;
    }
    if (item.requiredRole && !hasRole(item.requiredRole)) {
      return false;
    }
    return true;
  });

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleNotificationMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationMenuAnchor(event.currentTarget);
  };

  const handleNotificationMenuClose = () => {
    setNotificationMenuAnchor(null);
  };

  const handleLogout = async () => {
    handleUserMenuClose();
    await logout();
    navigate('/login');
  };

  const handleNotificationClick = (notificationId: string) => {
    markNotificationAsRead(notificationId);
    handleNotificationMenuClose();
  };

  // Cerrar drawer móvil al navegar
  useEffect(() => {
    if (isMobile) {
      setMobileOpen(false);
    }
  }, [location.pathname, isMobile]);

  // Contenido del drawer
  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header del drawer */}
      <Box sx={{ p: sidebarCollapsed ? 1 : 2, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: sidebarCollapsed ? 'center' : 'space-between' }}>
        {!sidebarCollapsed && (
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="h6" color="primary" fontWeight="bold" noWrap>
                CRM WhatsApp
              </Typography>
              <Chip label="v1.0.0" size="small" color="primary" variant="outlined" sx={{ height: 20, fontSize: '0.7rem', flexShrink: 0 }} />
            </Box>
            <Typography variant="body2" color="text.secondary" noWrap>
              Cooperativa Cainguás
            </Typography>
            <Chip
              icon={<ChatIcon sx={{ fontSize: 14 }} />}
              label="+54 3755 218605"
              size="small"
              color="success"
              variant="outlined"
              sx={{ mt: 1, height: 22, fontSize: '0.75rem' }}
            />
          </Box>
        )}
        <Tooltip title={sidebarCollapsed ? 'Expandir menú' : 'Colapsar menú'} placement="right">
          <IconButton
            onClick={handleSidebarToggle}
            size="small"
            sx={{ display: { xs: 'none', md: 'flex' }, flexShrink: 0 }}
          >
            {sidebarCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Navegación principal */}
      <List sx={{ flex: 1, py: 1 }}>
        {filteredNavigationItems.map((item) => (
          <NavigationItem
            key={item.id}
            item={item}
            currentPath={location.pathname}
            onClick={() => navigate(item.path)}
            collapsed={sidebarCollapsed}
          />
        ))}
      </List>

      <Divider />

      {/* Navegación secundaria */}
      <List sx={{ py: 1 }}>
        <NavigationItem
          item={{
            id: 'profile',
            label: 'Mi Perfil',
            icon: <PersonIcon />,
            path: '/dashboard/profile',
          }}
          currentPath={location.pathname}
          onClick={() => navigate('/dashboard/profile')}
          collapsed={sidebarCollapsed}
        />
      </List>

      {/* Estado de conexión */}
      <Box sx={{ p: 1.5, borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: sidebarCollapsed ? 'center' : 'flex-start' }}>
        {sidebarCollapsed ? (
          <Tooltip title={isConnected ? 'Conectado' : 'Desconectado'} placement="right">
            <Box sx={{ color: isConnected ? 'success.main' : 'error.main', display: 'flex' }}>
              {isConnected ? <WifiIcon fontSize="small" /> : <WifiOffIcon fontSize="small" />}
            </Box>
          </Tooltip>
        ) : (
          <Chip
            icon={isConnected ? <WifiIcon /> : <WifiOffIcon />}
            label={isConnected ? 'Conectado' : 'Desconectado'}
            color={isConnected ? 'success' : 'error'}
            variant="outlined"
            size="small"
          />
        )}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* AppBar */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${currentDrawerWidth}px)` },
          ml: { md: `${currentDrawerWidth}px` },
          zIndex: theme.zIndex.drawer + 1,
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="abrir menú"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {filteredNavigationItems.find(item =>
              location.pathname === item.path ||
              (item.path !== '/dashboard' && location.pathname.startsWith(item.path))
            )?.label || 'Dashboard'}
          </Typography>

          {/* Salud del sistema (solo ADMIN) */}
          {hasRole('ADMIN') && <SystemHealthIndicator />}

          {/* Notificaciones */}
          <Tooltip title="Notificaciones">
            <IconButton
              color="inherit"
              onClick={handleNotificationMenuOpen}
              sx={{ mr: 1 }}
            >
              <Badge badgeContent={notifications?.length || 0} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>
          </Tooltip>

          {/* Menú de usuario */}
          <Tooltip title="Menú de usuario">
            <IconButton
              onClick={handleUserMenuOpen}
              sx={{ p: 0 }}
            >
              <Avatar
                {...(user?.fullName || user?.username ? { alt: user?.fullName || user?.username } : {})}
                {...(user?.avatar && { src: user.avatar })}
                sx={{
                  bgcolor: 'secondary.main',
                  width: 32,
                  height: 32,
                }}
              >
                {((user?.fullName || user?.username)?.charAt(0)?.toUpperCase()) || ''}
              </Avatar>
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      {/* Drawer de navegación */}
      <Box
        component="nav"
        sx={{
          width: { md: currentDrawerWidth },
          flexShrink: { md: 0 },
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          {drawerContent}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: currentDrawerWidth,
              overflowX: 'hidden',
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
              }),
            },
          }}
          open
        >
          {drawerContent}
        </Drawer>
      </Box>

      {/* Contenido principal */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${currentDrawerWidth}px)` },
          minHeight: '100vh',
          bgcolor: 'background.default',
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Toolbar />
        <Box sx={{ p: 3 }}>
          <Outlet />
        </Box>
      </Box>

      {/* Menú de usuario */}
      <Menu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={handleUserMenuClose}
        onClick={handleUserMenuClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => navigate('/dashboard/profile')}>
          <PersonIcon sx={{ mr: 1 }} />
          Mi Perfil
        </MenuItem>
        <MenuItem onClick={handleEndShiftOpen} sx={{ color: 'warning.main' }}>
          <EndShiftIcon sx={{ mr: 1 }} />
          Finalizar turno
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <LogoutIcon sx={{ mr: 1 }} />
          Cerrar Sesión
        </MenuItem>
      </Menu>

      {/* Diálogo de confirmación — Finalizar turno */}
      <Dialog open={endShiftDialog} onClose={handleEndShiftClose} maxWidth="xs" fullWidth>
        <DialogTitle>Finalizar turno</DialogTitle>
        <DialogContent>
          {endShiftResult ? (
            <Alert severity="success" sx={{ mt: 1 }}>
              {endShiftResult.message}
              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                Cerrando sesión automáticamente...
              </Typography>
            </Alert>
          ) : endShiftError ? (
            <Alert severity="error" sx={{ mt: 1 }}>
              {endShiftError}
            </Alert>
          ) : (
            <Typography>
              Esto liberará <strong>todas tus conversaciones activas</strong> y las dejará disponibles para otros operadores. ¿Confirmás?
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          {!endShiftResult && (
            <>
              <Button onClick={handleEndShiftClose}>
                Cancelar
              </Button>
              <Button
                variant="contained"
                color="warning"
                onClick={handleEndShiftConfirm}
                disabled={endShiftLoading}
              >
                {endShiftLoading ? 'Liberando...' : 'Finalizar turno'}
              </Button>
            </>
          )}
          {endShiftError && (
            <Button onClick={handleEndShiftClose}>
              Cerrar
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Menú de notificaciones */}
      <Menu
        anchorEl={notificationMenuAnchor}
        open={Boolean(notificationMenuAnchor)}
        onClose={handleNotificationMenuClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            maxWidth: 360,
            maxHeight: 400,
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {!notifications || notifications.length === 0 ? (
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">
              No hay notificaciones
            </Typography>
          </MenuItem>
        ) : (
          notifications?.slice(0, 5).map((notification) => (
            <MenuItem
              key={notification.id}
              onClick={() => handleNotificationClick(notification.id)}
              sx={{
                whiteSpace: 'normal',
                maxWidth: 320,
              }}
            >
              <Box>
                <Typography variant="body2" fontWeight="bold">
                  {notification.title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {notification.message}
                </Typography>
              </Box>
            </MenuItem>
          ))
        )}
      </Menu>
    </Box>
  );
};

export default DashboardLayout;
